import React, { useEffect, useMemo, useState } from 'react';
import authService from '../../services/authService';

const RoleManagement = ({ userId }) => {
  const [roleGroups, setRoleGroups] = useState({});
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [initialRoles, setInitialRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    loadData();
  }, [userId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const rolesResponse = await authService.get('/roles');
      const userRolesResponse = await authService.get(`/roles/user/${userId}`);
      setRoleGroups(rolesResponse.data);
      setSelectedRoles(userRolesResponse.data);
      setInitialRoles(userRolesResponse.data);
    } catch (err) {
      console.error(err);
      setError('Ошибка загрузки ролей');
    } finally {
      setLoading(false);
    }
  };

  const addedRoles = useMemo(() => {
    return selectedRoles.filter(role => !initialRoles.includes(role));
  }, [selectedRoles, initialRoles]);

  const removedRoles = useMemo(() => {
    return initialRoles.filter(role => !selectedRoles.includes(role));
  }, [selectedRoles, initialRoles]);

  const hasChanges = addedRoles.length > 0 || removedRoles.length > 0;

  const toggleRole = (roleName) => {
    setSelectedRoles(prev => {
      if (prev.includes(roleName)) {
        return prev.filter(r => r !== roleName);
      }
      return [...prev, roleName];
    });
  };

  const selectAllInGroup = (roles) => {
    const roleNames = roles.map(r => r.roleName);
    setSelectedRoles(prev => [...new Set([...prev, ...roleNames])]);
  };

  const deselectAllInGroup = (roles) => {
    const roleNames = roles.map(r => r.roleName);
    setSelectedRoles(prev => prev.filter(r => !roleNames.includes(r)));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await authService.post('/roles/update-user-roles', {
        userId,
        roles: selectedRoles
      });
      setInitialRoles(selectedRoles);
      setSuccess('Роли успешно сохранены');
    } catch (err) {
      console.error(err);
      setError('Ошибка сохранения ролей');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Загрузка...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-2 pb-5">
      {/* Alerts */}
      {error && (
        <div className="alert alert-danger d-flex align-items-center" role="alert">
          <svg className="bi flex-shrink-0 me-2" width="24" height="24" role="img" aria-label="Ошибка:">
            <use xlinkHref="#exclamation-triangle-fill"/>
          </svg>
          <div>{error}</div>
        </div>
      )}

      {success && (
        <div className="alert alert-success d-flex align-items-center" role="alert">
          <svg className="bi flex-shrink-0 me-2" width="24" height="24" role="img" aria-label="Успех:">
            <use xlinkHref="#check-circle-fill"/>
          </svg>
          <div>{success}</div>
        </div>
      )}

      {/* Changes Summary */}
      {hasChanges && (
        <div className="card mb-4 border-light shadow-sm">
          <div className="card-header bg-white border-bottom-0">
            <h5 className="card-title mb-0">Изменения ролей</h5>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-6">
                <small className="text-success fw-medium d-block mb-2">Добавленные роли</small>
                <div className="d-flex flex-wrap gap-1">
                  {addedRoles.length > 0 ? addedRoles.map(role => (
                    <span key={role} className="badge bg-success bg-opacity-10 text-success px-2 py-1">
                      + {role}
                    </span>
                  )) : (
                    <span className="text-muted small">Нет изменений</span>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <small className="text-danger fw-medium d-block mb-2">Удаленные роли</small>
                <div className="d-flex flex-wrap gap-1">
                  {removedRoles.length > 0 ? removedRoles.map(role => (
                    <span key={role} className="badge bg-danger bg-opacity-10 text-danger px-2 py-1">
                      − {role}
                    </span>
                  )) : (
                    <span className="text-muted small">Нет изменений</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Role Groups Grid */}
      <div className="row g-4">
        {Object.entries(roleGroups).map(([groupKey, roles]) => {
          const groupDescription = roles?.[0]?.groupDescription;
          const selectedCount = roles.filter(role => 
            selectedRoles.includes(role.roleName)
          ).length;

          return (
            <div key={groupKey} className="col-12 col-md-6 col-xl-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-header bg-primary bg-gradient text-white py-3">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h5 className="mb-1">{groupKey}</h5>
                      {groupDescription && (
                        <small className="text-white-50">{groupDescription}</small>
                      )}
                    </div>
                    <span className="badge bg-light text-primary rounded-pill">
                      {selectedCount} / {roles.length}
                    </span>
                  </div>
                </div>

                <div className="card-body p-0">
                  <div className="d-flex gap-2 p-3 border-bottom bg-light">
                    <button
                      onClick={() => selectAllInGroup(roles)}
                      className="btn btn-sm btn-outline-secondary"
                    >
                      Выбрать всё
                    </button>
                    <button
                      onClick={() => deselectAllInGroup(roles)}
                      className="btn btn-sm btn-outline-secondary"
                    >
                      Снять всё
                    </button>
                  </div>

                  <div className="p-3">
                    {roles.map(role => {
                      const checked = selectedRoles.includes(role.roleName);
                      const added = addedRoles.includes(role.roleName);
                      const removed = removedRoles.includes(role.roleName);

                      return (
                        <label
                          key={role.roleName}
                          className={`d-flex align-items-start p-2 rounded-3 mb-2 ${
                            added ? 'bg-success bg-opacity-10 border border-success border-opacity-25' :
                            removed ? 'bg-danger bg-opacity-10 border border-danger border-opacity-25' :
                            checked ? 'bg-primary bg-opacity-10 border border-primary border-opacity-25' :
                            'border border-transparent'
                          }`}
                          role="button"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleRole(role.roleName)}
                            className="form-check-input mt-1 me-2 flex-shrink-0"
                          />
                          <div className="flex-grow-1 min-w-0">
                            <div className="small fw-medium text-truncate">
                              {role.roleDescription}
                            </div>
                            <div className="small text-muted text-truncate">
                              {role.roleName}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Sticky Save Button */}
      <div 
        className="position-fixed bottom-0 start-0 end-0 bg-white border-top shadow-lg py-3 px-3"
        style={{ zIndex: 1030 }}
      >
        <div className="container-fluid d-flex justify-content-between align-items-center">
          <div>
            <div className="fw-semibold">Изменения ролей</div>
            <small className="text-muted">
              Добавлено: {addedRoles.length} • Удалено: {removedRoles.length}
            </small>
          </div>
          <button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="btn btn-primary px-4 py-2"
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Сохранение...
              </>
            ) : (
              'Сохранить изменения'
            )}
          </button>
        </div>
      </div>

      {/* Bottom padding to prevent content from being hidden behind sticky footer */}
      <div style={{ height: '80px' }}></div>
    </div>
  );
};

export default RoleManagement;